import type { Schemas } from '@argzombie/api-client';
import {
  DEFAULT_RESURRECTION_BANDS,
  blockAsParsed,
  charactersDefaults,
  resurrectionDefaults,
  resurrectionKitDefaults,
  type ResurrectionKitWind,
} from '../../../lib/teamsDefaults';
import {
  CHARACTER_DELETE_CONFIRMS,
  MAX_CHARACTER_SLOTS,
  STORE_PRODUCT_KINDS,
  type CharactersRulesDef,
  type ResurrectionBandDef,
  type ResurrectionKitDef,
  type ResurrectionRulesDef,
  type StoreProductDef,
} from '../../../lib/validate/types';
import { useDraft } from '../../../lib/draft';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
} from '../fields';
import type { FormProps } from '../types';
import { TierSelectField } from '../wind/WindForms';

/**
 * Characters and paid resurrection (ADR 0069). The forms for the `characters` and `resurrection` sections, and the
 * Store form that sets each product's `kind` and `tier` (everything else in a product stays in Edit JSON). Issues come
 * from `lib/validate/characters.ts`, a port of shared-cs `ContentValidator.Characters.cs`.
 */

/** `characters`: slot caps (zombies count toward them), the delete confirm and friendships across characters. */
export function CharactersForm({ value, onChange, errors }: FormProps<CharactersRulesDef>) {
  const c = blockAsParsed(charactersDefaults, value ?? undefined)!;
  const set = (patch: Partial<CharactersRulesDef>) => onChange({ ...c, ...patch });
  return (
    <Fieldset legend="Slots">
      <NumberField
        label="Slots (no pass)"
        hint={`Characters an account can hold without the VIP pass, alive and zombie together (1..vipSlots).`}
        integer
        min={1}
        max={MAX_CHARACTER_SLOTS}
        value={c.maxSlots}
        issues={errors['maxSlots']}
        onChange={(v) => set({ maxSlots: v ?? 0 })}
      />
      <NumberField
        label="Slots with the VIP pass"
        hint={`The cap while the VIP pass (supporter_monthly) is active (1..${MAX_CHARACTER_SLOTS}). Slots above the cap freeze when it lapses.`}
        integer
        min={1}
        max={MAX_CHARACTER_SLOTS}
        value={c.vipSlots}
        issues={errors['vipSlots']}
        onChange={(v) => set({ vipSlots: v ?? 0 })}
      />
      <SelectField
        label="Delete confirm"
        hint="What the player types to delete a character: name = the character's name."
        value={c.deleteConfirm}
        options={CHARACTER_DELETE_CONFIRMS.map((d) => ({ value: d, label: d }))}
        issues={errors['deleteConfirm']}
        onChange={(v) => set({ deleteConfirm: v ?? '' })}
      />
      <CheckboxField
        label="Friendships on every character"
        hint="On: a QR exchange gives a copy of the friend to every alive character with room on both sides (the active one first). Off: the active character on each side only. The pair cooldown is per account either way."
        checked={c.friendshipsAllCharacters ?? true}
        onChange={(v) => set({ friendshipsAllCharacters: v })}
      />
    </Fieldset>
  );
}

const KIT_NUMBERS: [keyof ResurrectionKitDef, string, string][] = [
  ['foodDays', 'Food (days)', 'Days of food for the whole crew.'],
  ['waterDays', 'Water (days)', 'Days of water for the whole crew.'],
  ['magazinesForBestGun', 'Magazines', 'Magazines for the best gun the character owns.'],
  ['bandages', 'Bandages', 'Bandages delivered.'],
];
const KIT_FLAGS: [keyof (ResurrectionKitDef & ResurrectionKitWind), string, string][] = [
  [
    'weaponFromLootTier',
    'A weapon',
    'One weapon at the loot tier of the day the character reached.',
  ],
  ['fullyRested', 'Fully rested', 'WIND: the character comes back with no rest debt.'],
  ['fullHealth', 'Full health (old)', 'Old, kept one content version: fully rested replaces it.'],
  ['clearConditions', 'Clear conditions', 'Wounds, sickness and bites are cleared.'],
];

/** The store products of kind `resurrection`, by tier, from the draft (the ladder the bands price into). */
function ladder(products: (StoreProductDef | null)[] | null | undefined) {
  const byTier = new Map<number, string[]>();
  for (const p of products ?? []) {
    if (p?.kind !== 'resurrection' || p.tier == null) continue;
    byTier.set(p.tier, [...(byTier.get(p.tier) ?? []), p.id ?? '?']);
  }
  return byTier;
}

/**
 * `resurrection`: the days-alive bands that set the price tier (C1), the boost kit (C4), and the cutscene switch. Empty
 * `bands` means the C# defaults (shown); the first edit fills them in, since one band would otherwise replace all eight.
 */
export function ResurrectionForm({ value, onChange, errors }: FormProps<ResurrectionRulesDef>) {
  const r = blockAsParsed(resurrectionDefaults, value ?? undefined)!;
  const set = (patch: Partial<ResurrectionRulesDef>) => onChange({ ...r, ...patch });
  const own = (r.bands?.length ?? 0) > 0;
  const bands: ResurrectionBandDef[] = own
    ? (r.bands ?? []).map((b) => b ?? {})
    : DEFAULT_RESURRECTION_BANDS.map((b) => ({ ...b }));
  const kit: ResurrectionKitDef & ResurrectionKitWind = blockAsParsed(
    resurrectionKitDefaults,
    r.kit ?? undefined,
  )!;
  const setKit = (patch: Partial<ResurrectionKitDef & ResurrectionKitWind>) =>
    set({ kit: { ...kit, ...patch } });
  const draft = useDraft();
  const products = ladder(draft?.bundle.store?.products as StoreProductDef[] | undefined);

  return (
    <>
      <Fieldset legend="Price bands (days alive → tier)">
        <ListEditor
          label="Bands"
          what="band"
          hint={
            own
              ? 'Ascending minDays from 0; band N has tier N. The quote uses the band of the days the character survived.'
              : 'Showing the C# defaults (bands is empty in the draft). Editing a band writes all of them into the draft.'
          }
          items={bands}
          issues={errors['bands']}
          make={() => ({ minDays: (bands.at(-1)?.minDays ?? -1) + 1, tier: bands.length + 1 })}
          onChange={(next) => set({ bands: next })}
          row={(b, i, setBand) => (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label className="flex items-center gap-1">
                From day
                <NumberInput
                  label={`Band ${i + 1} min days`}
                  integer
                  min={0}
                  value={b.minDays}
                  invalid={!!errors[`bands.${i}.minDays`]}
                  onChange={(v) => setBand({ ...b, minDays: v ?? 0 })}
                />
              </label>
              <label className="flex items-center gap-1">
                tier
                <NumberInput
                  label={`Band ${i + 1} tier`}
                  integer
                  min={1}
                  value={b.tier}
                  invalid={!!errors[`bands.${i}.tier`]}
                  onChange={(v) => setBand({ ...b, tier: v ?? 0 })}
                />
              </label>
              <span className="text-xs text-bruise">
                product: {products.get(b.tier ?? 0)?.join(', ') ?? 'none in the store'}
              </span>
              <IssueList
                issues={[
                  ...(errors[`bands.${i}.minDays`] ?? []),
                  ...(errors[`bands.${i}.tier`] ?? []),
                ]}
              />
            </div>
          )}
        />
      </Fieldset>
      <Fieldset legend="Boost kit">
        {KIT_NUMBERS.map(([k, label, hint]) => (
          <NumberField
            key={k}
            label={label}
            hint={`${hint} 0..999.`}
            integer
            min={0}
            max={999}
            value={kit[k] as number | undefined}
            issues={errors[`kit.${k}`]}
            onChange={(v) => setKit({ [k]: v ?? 0 })}
          />
        ))}
        {KIT_FLAGS.map(([k, label, hint]) => (
          <CheckboxField
            key={k}
            label={label}
            hint={hint}
            checked={!!kit[k]}
            onChange={(v) => setKit({ [k]: v })}
          />
        ))}
        <TierSelectField
          label="Comes back at (not fully rested)"
          hint="WIND: the tier the character comes back at when Fully rested is off (rest debt = the tier's index)."
          value={kit.restTier}
          onChange={(v) => setKit({ restTier: v })}
        />
        <IssueList issues={errors['kit']} />
      </Fieldset>
      <Fieldset legend="Cutscene">
        <CheckboxField
          label="Show the turning cutscene once"
          hint="The death cutscene plays once per character (in the raid at the moment of death, or on select after starving)."
          checked={!!r.showCutsceneOnce}
          onChange={(v) => set({ showCutsceneOnce: v })}
        />
      </Fieldset>
    </>
  );
}

type Store = Omit<Schemas['StoreDef'], 'products'> & {
  products?: (StoreProductDef | null)[] | null;
};

/**
 * `store`: each product's kind and, for a `resurrection` product, its tier (one product per tier, ADR 0069 C2). Energy,
 * store ids, caps and the rest stay in Edit JSON.
 */
export function StoreForm({ value, onChange, errors }: FormProps<Store>) {
  const s = value ?? {};
  const products = s.products ?? [];
  const setProduct = (i: number, p: StoreProductDef) =>
    onChange({ ...s, products: products.map((x, j) => (j === i ? p : x)) });
  return (
    <Fieldset legend="Products">
      <p className="text-xs text-bruise sm:col-span-2">
        Kind and resurrection tier per product. Energy, Apple and Google ids and the store caps are
        edited in Edit JSON.
      </p>
      <table className="w-full text-sm sm:col-span-2">
        <thead className="text-left text-xs text-bruise">
          <tr>
            <th>Product</th>
            <th>Kind</th>
            <th>Tier</th>
            <th>Energy</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) =>
            p == null ? null : (
              <tr key={i} className="border-t border-newsprint align-top">
                <td className="py-1 font-mono">{p.id}</td>
                <td className="py-1">
                  <SelectInput
                    label={`${p.id} kind`}
                    value={p.kind}
                    options={STORE_PRODUCT_KINDS.map((k) => ({ value: k, label: k }))}
                    onChange={(kind) =>
                      setProduct(i, {
                        ...p,
                        kind: kind ?? '',
                        tier: kind === 'resurrection' ? (p.tier ?? 1) : null,
                      })
                    }
                  />
                </td>
                <td className="py-1">
                  {p.kind === 'resurrection' || p.tier != null ? (
                    <NumberInput
                      label={`${p.id} tier`}
                      integer
                      min={1}
                      nullable
                      value={p.tier}
                      invalid={!!errors[`products.${i}.tier`]}
                      onChange={(tier) => setProduct(i, { ...p, tier })}
                    />
                  ) : (
                    '-'
                  )}
                  <IssueList issues={errors[`products.${i}.tier`]} />
                </td>
                <td className="py-1 font-mono">{p.energy}</td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      <IssueList issues={errors['products']} />
    </Fieldset>
  );
}
